/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import {
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  InputAdornment,
  Switch,
} from "@mui/material";
import {
  Settings as SettingsIcon,
  Language as LanguageIcon,
  BusinessCenter as ClientTypeIcon,
  Category as CategoryIcon,
  Image as ImageIcon,
} from "@mui/icons-material";
import PriorityColumns from "./PriorityColumns";

const subCategoryOptions = {
  "Retail & E-commerce": [
    "Fashion & Apparel",
    "Footwear",
    "Electronics",
    "Beauty & Skincare",
    "Furniture & Home Decor",
    "Jewellery",
    "Eyewear",
    "Pet Supplies",
    "Kids' Products",
    "Grocery & Essentials",
  ],
  "Health & Wellness": [
    "Supplements & Vitamins",
    "Skincare Clinics",
    "Mental Wellness Apps",
    "Fitness Equipment",
    "Weight Loss Programs",
  ],
  "Tech & Software": [
    "SaaS Tools",
    "CRM & Marketing Automation",
    "Cybersecurity Solutions",
    "Web Hosting & Domains",
    "Mobile Apps",
  ],
  "Education & Training": [
    "Online Courses",
    "Test Prep",
    "Study Abroad Consultants",
    "Coding Bootcamps",
    "Language Learning",
  ],
  "Real Estate & Home Services": [
    "Real Estate Portals",
    "Property Developers",
    "Interior Design Services",
    "Pest Control",
    "Home Cleaning Services",
  ],
  "Animals & Pets": [
    "Pet Food",
    "Veterinary Services",
    "Pet Grooming",
    "Animal Shelters",
  ],
  Automotive: [
    "Car Dealerships",
    "Car Rentals",
    "Electric Vehicle Brands",
    "Auto Accessories",
    "Insurance Providers",
  ],
  "Finance & Legal": [
    "Digital Banks",
    "Loan Providers",
    "Stock Trading Platforms",
    "Tax & Compliance Services",
    "Legal Services",
  ],
  "Travel & Hospitality": [
    "Flight & Hotel Booking",
    "Visa Consultants",
    "Holiday Packages",
    "Local Experiences",
    "Cruises",
  ],
  "B2B & Industrial": [
    "Logistics & Supply Chain",
    "HR & Payroll Software",
    "Industrial Equipment",
    "Business Loans & Credit",
    "Franchise Opportunities",
  ],
};

const currencies = [
  "USD",
  "EUR",
  "GBP",
  "JPY",
  "AUD",
  "CAD",
  "CHF",
  "CNY",
  "SEK",
  "NZD",
  "INR",
];
const clientTypes = ["Lead Generation", "Revenue Based"];
const categories = Object.keys(subCategoryOptions);
const apiToUIClientType = { Lead: "Lead Generation", Revenue: "Revenue Based" };
const uiToApiClientType = {
  "Lead Generation": "Lead",
  "Revenue Based": "Revenue",
};
const currencySymbolMap = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  JPY: "¥",
  INR: "₹",
  AUD: "A$",
  CAD: "C$",
  CHF: "Fr",
  CNY: "¥",
  SEK: "kr",
  NZD: "NZ$",
};

// Helper function to handle string-to-boolean conversion for Local Storage safely
const getShowLogoPref = (id) => {
  if (!id) return true; // Default to true if no project
  const storedVal = localStorage.getItem(`showLogoProject_${id}`);
  if (!storedVal || storedVal === "false") return false;
  return true; // Default to true if "true" or null
};

export default function ProjectConfiguration() {
  const [budgetCurrency, setBudgetCurrency] = useState(
    String(localStorage.getItem("customer_id")) === "7948821642"
      ? "USD"
      : "INR",
  );
  const [projectId, setProjectId] = useState(
    localStorage.getItem("project_id") || "",
  );

  const [category, setCategory] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [website, setWebsite] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [clientType, setClientType] = useState("Lead Generation");

  // Initialize state directly from our helper
  const [showProjectIcon, setShowProjectIcon] = useState(() => getShowLogoPref(localStorage.getItem("project_id")));

  const [loadingProjectConfig, setLoadingProjectConfig] = useState(false);
  const [initialDataLoading, setInitialDataLoading] = useState(true);
  const [selectedMonthYear, setSelectedMonthYear] = useState(new Date());

  useEffect(() => {
    if (!projectId) {
      setInitialDataLoading(false);
      setCategory("");
      setSubCategory("");
      setWebsite("");
      setCurrency("USD");
      setClientType("Lead Generation");
      setShowProjectIcon(false);
      return;
    }

    // Sync toggle immediately if project ID changes
    setShowProjectIcon(getShowLogoPref(projectId));

    setInitialDataLoading(true);
    const fetchSettings = async () => {
      try {
        const response = await fetch(
          "https://api.confidanto.com/project-settings/fetch",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              projectId: projectId,
              email: localStorage.getItem("email"),
              customer_id: localStorage.getItem("customer_id"),
            }),
          },
        );
        if (response.status === 404) {
          setCategory("");
          setSubCategory("");
          setWebsite("");
          setCurrency("USD");
          setClientType("Lead Generation");
          return;
        }
        if (!response.ok) throw new Error(`Failed to fetch`);

        const data = await response.json();
        setCategory(data.category || "");
        if (
          data.category &&
          subCategoryOptions[data.category]?.includes(data.sub_category)
        ) {
          setSubCategory(data.sub_category || "");
        } else {
          setSubCategory("");
        }
        setWebsite(data.website || "");
        setCurrency(data.currency || "USD");
        setClientType(apiToUIClientType[data.client_type] || "Lead Generation");
      } catch (err) {
        console.error("Fetch error:", err);
      } finally {
        setInitialDataLoading(false);
      }
    };

    fetchSettings();
  }, [projectId]);

  const handleSaveProjectConfig = async () => {
    if (!projectId) return;
    setLoadingProjectConfig(true);

    // Save to local storage explicitly as a string
    localStorage.setItem(`showLogoProject_${projectId}`, showProjectIcon ? "true" : "false");

    const payload = {
      projectId: projectId,
      email: localStorage.getItem("email"),
      customer_id: localStorage.getItem("customer_id"),
      client_type: uiToApiClientType[clientType] || clientType,
      category,
      sub_category: subCategory,
      website,
      selected_month: selectedMonthYear.getMonth() + 1,
      selected_year: selectedMonthYear.getFullYear(),
    };

    try {
      const saveRes = await fetch(
        "https://api.confidanto.com/project-settings/update",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
      if (saveRes.ok) {
        alert("Project settings updated successfully!");
        setBudgetCurrency(currency);
      }
    } catch (err) {
      console.error("Save error:", err);
    } finally {
      setLoadingProjectConfig(false);
    }
  };

  const iconClasses = "mr-1 text-gray-500";
  const textFieldClasses = "w-full rounded-lg";
  const saveButtonBaseClasses =
    "font-medium normal-case text-[0.9375rem] py-[10px] px-6 rounded-lg min-w-[100px]";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="bg-white/80 backdrop-blur-md border border-gray-200 shadow-xl rounded-2xl overflow-hidden">
          <div className="flex items-center gap-3 px-6 py-5 border-b bg-white/60 backdrop-blur">
            <div className="p-2 bg-blue-100 rounded-lg">
              <SettingsIcon className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-800">
                Project Configuration
              </h2>
              <p className="text-sm text-gray-500">
                Manage your project settings and preferences
              </p>
            </div>
          </div>

          <form noValidate autoComplete="off" className="p-4 md:p-6">
            {initialDataLoading && projectId ? (
              <div className="flex justify-center items-center min-h-[200px]">
                <CircularProgress />
              </div>
            ) : (
              <div className="grid grid-cols-12 gap-5">
                <div className="col-span-12 md:col-span-6">
                  <TextField
                    select
                    fullWidth
                    label="Category"
                    value={category}
                    onChange={(e) => {
                      const newCategory = e.target.value;
                      setCategory(newCategory);
                      const newSubOptions = subCategoryOptions[newCategory];
                      setSubCategory(newSubOptions?.[0] || "");
                    }}
                    variant="outlined"
                    className={textFieldClasses}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CategoryIcon className={iconClasses} />
                        </InputAdornment>
                      ),
                    }}
                  >
                    {categories.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </div>

                <div className="col-span-12 md:col-span-6">
                  <TextField
                    select
                    fullWidth
                    label="Subcategory"
                    value={subCategory}
                    onChange={(e) => setSubCategory(e.target.value)}
                    variant="outlined"
                    className={textFieldClasses}
                    disabled={!category}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CategoryIcon className={iconClasses} />
                        </InputAdornment>
                      ),
                    }}
                  >
                    {category &&
                      subCategoryOptions[category]?.map((sub) => (
                        <MenuItem key={sub} value={sub}>
                          {sub}
                        </MenuItem>
                      ))}
                  </TextField>
                </div>

                <div className="col-span-12 md:col-span-6">
                  <TextField
                    fullWidth
                    label="Website / Domain Name"
                    variant="outlined"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    placeholder="https://yourdomain.com"
                    className={textFieldClasses}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LanguageIcon className={iconClasses} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>

                <div className="col-span-12 md:col-span-6">
                  <TextField
                    select
                    fullWidth
                    label="Client Type"
                    value={clientType}
                    onChange={(e) => setClientType(e.target.value)}
                    variant="outlined"
                    className={textFieldClasses}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ClientTypeIcon className={iconClasses} />
                        </InputAdornment>
                      ),
                    }}
                  >
                    {clientTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </TextField>
                </div>

                {/* Styled Switch Section */}
                <div className="col-span-12 md:col-span-6">
                  <div className="flex items-center justify-between border border-gray-300 rounded-lg h-[56px] px-3 bg-white hover:border-gray-400 transition-colors">
                    <div className="flex items-center gap-2">
                      <ImageIcon className="text-gray-500 ml-1" />
                      <span className="text-sm font-medium text-gray-600">
                        Show Icon in Reports
                      </span>
                    </div>
                    <Switch
                      checked={showProjectIcon}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        setShowProjectIcon(isChecked);
                        // Optionally update local storage instantly on click so it doesn't wait for "Save Settings"
                        localStorage.setItem(`showLogoProject_${projectId}`, isChecked ? "true" : "false");
                      }}
                      color="primary"
                    />
                  </div>
                </div>

                <div className="col-span-12 pt-2 md:pt-4 flex justify-start">
                  <Button
                    variant="contained"
                    onClick={handleSaveProjectConfig}
                    disabled={
                      loadingProjectConfig || initialDataLoading || !projectId
                    }
                    className={`${saveButtonBaseClasses} bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-70`}
                  >
                    {loadingProjectConfig ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Save Settings"
                    )}
                  </Button>
                </div>
              </div>
            )}
          </form>
        </div>
        <PriorityColumns />
      </div>
    </div>
  );
}